import { useGlobalSearchParams, usePathname, type Href } from 'expo-router';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { trackEvent, useFeatureFlagVariant } from '@/modules/analytics';
import { fetchUser, getValidAccessToken, subscribeToUnauthorized } from '@/modules/api';
import { getNativeAppClientInfo } from '@/modules/clientContext';
import type { User } from '@/types/user';

import { DEFAULT_ONBOARDING_DRAFT } from '../constants/defaultOnboardingDraft';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import { useAnalyticsIdentify } from '../hooks/useAnalyticsIdentify';
import { useCoachMatching } from '../hooks/useCoachMatching';
import { useEligibilityCheck } from '../hooks/useEligibilityCheck';
import { useOnboardingResources } from '../hooks/useOnboardingResources';
import { useProgramMatching } from '../hooks/useProgramMatching';
import { usePurl } from '../hooks/usePurl';
import type { AuthStatus } from '../types/authStatus';
import { OnboardingDataDraft } from '../types/onboardingDataDraft';
import { type GoToNextStepOptions } from '../types/onboardingStep';
import type { PartnersState } from '../types/partnersState';
import type { ResourceState, Resolution } from '../types/resolution';
import { UNSET } from '../types/resolution';
import type { RulesEngineContextType } from '../types/rulesEngineContextType';
import { dynamicSurveyNextStep, isDynamicSurveyStep } from '../utils/dynamicSurveyNextStep';
import { hrefPathname } from '../utils/hrefPathname';
import { isMemberContactComplete } from '../utils/isMemberContactComplete';
import { mapUserDataToDraft } from '../utils/mapUserDataToDraft';
import { maybeRedirectToWebApp } from '../utils/maybeRedirectToWebApp';
import { findNextStep } from '../utils/onboardingStepProgress';
import { programMatchingNextStep } from '../utils/programMatchingNextStep';
import { redeemNativeHandoff } from '../utils/redeemNativeHandoff';
import { resolveOnboardingRoute } from '../utils/resolveOnboardingRoute';
import { safeDecodeOrgParam } from '../utils/safeDecodeOrgParam';
import { uuidFromUrn } from '../utils/uuidFromUrn';

export const RulesEngineContext = createContext<RulesEngineContextType | null>(null);

export const RulesEngineProvider = ({ children }: { children: React.ReactNode }) => {
  // --- Shared core state (cross-cutting; read by the driver + many features) ---
  const [draft, setDraft] = useState<OnboardingDataDraft>({ ...DEFAULT_ONBOARDING_DRAFT });
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [currentRoute, setCurrentRoute] = useState<Href | null>(null);
  // Visited-step history + back navigation live in OnboardingStepsProvider
  // (a child of this provider) since they're a pure function of currentRoute.
  const [userData, setUserData] = useState<User>();
  const [partners, setPartners] = useState<PartnersState>({
    status: 'unset',
    data: null,
    error: null,
  });

  // `?org=` (+ `?token=` for PURL) from the URL. Hoisted above the effect cluster
  // (hook-ordering convention) so it can feed the eligibility / PURL hooks + the
  // driver.
  const { org: orgParam, token: tokenParam } = useGlobalSearchParams<{
    org?: string;
    token?: string;
  }>();
  const pathname = usePathname();
  const shouldReadOrgParam =
    pathname === '/' ||
    pathname === '/step/account-creation' ||
    pathname === '/step/company-search';
  const decodedOrgParam = shouldReadOrgParam ? safeDecodeOrgParam(orgParam) : null;
  // The PURL token is read on every pre-account route a `?token=&org=` deep link
  // can land on — the cold-start entry (`/`), the account-creation route (Sparta
  // may redirect here directly), and the dedicated DOB-verification route (so a
  // refresh there re-validates — the layout keeps the URL params while
  // `currentRoute` matches the pathname). Reading it before navigation is what
  // lets the resolver route a no-account deep-link user to
  // `/step/purl-dob-verification`. Once validated, the token is held in usePurl
  // state, so later screens read it from there, not the URL. A validated token on
  // an already-authenticated user is harmless — the resolver's PURL branch only
  // fires pre-account.
  const shouldReadPurlToken =
    pathname === '/' ||
    pathname === '/step/account-creation' ||
    pathname === '/step/purl-dob-verification';
  const purlTokenParam = shouldReadPurlToken ? tokenParam : undefined;

  // SSO users carry their paying org on `customerProfile.ssoPayingOrganization`
  // (no top-level payingOrganization). Its bare uuid is matched against the
  // catalog's `webserverOrgUuid` to skip company-search. A primitive string, so
  // it's safe to recompute each render — feeds the eligibility hook + the driver
  // without churning their memo identities.
  const ssoWebserverOrgUuid = uuidFromUrn(
    userData?.customerProfile?.ssoPayingOrganization?.urn ?? undefined
  );

  // Launch-context embed flags (Sparta's getNativeAppClientInfo). Read here so
  // this provider is the single source of truth and screens never re-read the
  // URL. The util captures the launch params at module load (before any redirect
  // can strip them — see launchParams) and freezes them, returning a stable
  // object, so it stays correct even though the URL drops the params afterward.
  const clientInfo = getNativeAppClientInfo();

  // --- Core-owned mirror refs (read by goToNextStep / the driver / completion) ---
  // The core sync effect below mirrors feature state (read from the hook returns)
  // into these so the stable callbacks read fresh values without listing them.
  const authStatusRef = useRef(authStatus);
  const userDataRef = useRef(userData);
  const currentRouteRef = useRef(currentRoute);
  const eligibilityCheckResultRef =
    useRef<RulesEngineContextType['eligibilityCheckResult']>(undefined);
  const hasProgramRef = useRef<boolean | undefined>(undefined);
  const surveyCompleteRef = useRef<boolean | undefined>(undefined);
  const requiresCrisisLineRef = useRef(false);
  const showTestimonialPageRef = useRef(false);
  // Mirrors for the survey-start decision in forward nav (the resolver's survey
  // gate is arm-aware): the whole flag resource + the dynamic→legacy error latch.
  const dynamicAssessmentEnabledRef = useRef<ResourceState<boolean>>(UNSET);
  const dynamicAssessmentErroredRef = useRef(false);
  // Per-survey completion from the dynamic pass (see dynamicCompletedFlowKeys):
  // lets forward nav skip a standalone screener only when its flow actually
  // completed, not merely because we're in the test arm.
  const dynamicCompletedFlowKeysRef = useRef<readonly string[]>([]);
  // Guards the once-per-session analytics identify (see useAnalyticsIdentify).
  const hasIdentifiedRef = useRef(false);
  // Guards the completion chokepoint so it fires at most once per user session.
  const onboardingCompletedRef = useRef(false);
  // The solo care-team's uuid, captured by useOnboardingResources' ensureTeams,
  // read by coach matching's sendIntroMessage. Owned here so the consumer keeps
  // an eslint-stable ref; resources writes + clears it.
  const soloTeamUuidRef = useRef<string | undefined>(undefined);

  // --- Auth lifecycle (cross-cutting; the reset chokepoint sits above features) ---
  const setUser = useCallback((user: User | undefined) => {
    setUserData((prev) => {
      if (user === undefined) return undefined;
      const { customerProfile, ...rest } = user;
      return {
        ...prev,
        ...rest,
        customerProfile: {
          ...prev?.customerProfile,
          ...customerProfile,
        },
      };
    });
    setDraft((prev) => ({
      ...prev,
      ...mapUserDataToDraft(user),
    }));
  }, []);

  // Refresh the current user. Shrunk to its essence: fetch + sync. Routing is
  // owned entirely by the driver effect below. Throws on failure so callers can
  // branch (bootstrap → resetUserData; SignInBottomSheet → surface an error).
  const refreshUser = useCallback(async () => {
    const user = await fetchUser();
    setAuthStatus('authenticated');
    setUser(user);
    return user;
  }, [setUser]);

  // --- Feature hooks (each owns its local state + refs + actions + reset) ---
  // The rules-engine network layer: driver-fetched resource state + loaders.
  const {
    programInstances,
    metricPoints,
    teams,
    prescribedPrograms,
    matchedCoaches,
    coachAvailability,
    dynamicAssessmentEnabled,
    dynamicAssessmentErrored,
    markDynamicAssessmentErrored,
    dynamicCompletedFlowKeys,
    setDynamicCompletedFlowKeys,
    loadPartners,
    ensureProgramInstances,
    ensureMetricPoints,
    ensureTeams,
    ensurePrescribedPrograms,
    ensureMatchedCoaches,
    ensureCoachAvailability,
    ensureDynamicAssessment,
    rearmTeams,
    markSurveyComplete,
    markProgramEnrolled,
    markProviderMatchingComplete,
    resetResources,
  } = useOnboardingResources({ setPartners, userDataRef, soloTeamUuidRef });

  const {
    selectedOrg,
    selectedOrgRef,
    setSelectedOrg,
    restartEligibility,
    runEligibilityCheck,
    validateAndUseInviteCode,
    eligibilityCheckResult,
    setEligibilityCheckResult,
  } = useEligibilityCheck({
    draft,
    setDraft,
    partners,
    decodedOrgParam,
    orgParam,
    ssoWebserverOrgUuid,
    refreshUser,
    setCurrentRoute,
  });

  // PURL pre-authenticated enrollment (folded into the account-creation screen).
  // Owns token validation + sign-up; the screen reads `purl` to switch into
  // PURL mode and calls `submitPurlAccount` on Continue.
  const { purl, verifyDob, verifyAnotherWay, submitPurlAccount } = usePurl({
    tokenParam: purlTokenParam,
    orgParam,
    // Only validate a token once auth has settled to unauthenticated (the "user
    // endpoint returned 401" check) — a signed-in user falls through to normal nav.
    isUnauthenticated: authStatus === 'unauthenticated',
    refreshUser,
  });

  const program = useProgramMatching({
    prescribedPrograms,
    userDataRef,
    setCurrentRoute,
    rearmTeams,
    markSurveyComplete,
    markProgramEnrolled,
  });

  const coach = useCoachMatching({ matchedCoaches, userDataRef, soloTeamUuidRef });

  // Member Contact experiment (ME-1256) — the multivariate split, read on mount.
  const memberContactFlag = useFeatureFlagVariant<'control' | 'test'>(
    FEATURE_FLAGS.MEMBER_CONTACT_IN_ONBOARDING
  );

  // Session dismissal of the member-contact step (its Continue/Skip) lives here in
  // the state machine, not in the flag-reader hook. Cleared on per-user reset.
  const [memberContactDismissed, setMemberContactDismissed] = useState(false);
  const dismissMemberContact = useCallback(() => setMemberContactDismissed(true), []);

  // Whether to route the member-contact step in: experiment test variant, not
  // dismissed this session, and the member hasn't already provided their
  // address/phone (so a member with the info on file — or a returning user — is
  // never re-prompted).
  const showMemberContact =
    memberContactFlag.value === 'test' &&
    !memberContactDismissed &&
    !isMemberContactComplete(userData);

  // Stable `reset` references (bound to each hook's useCallback) for the reset
  // orchestrator's deps — pulling them out avoids depending on the hooks' (new
  // each render) return objects.
  const { reset: resetProgram } = program;
  const { reset: resetCoach } = coach;
  // Stable references consumed by goToNextStep's survey-exhaustion branch.
  const { enterCrisisProgram, pendingCrisisUrnsRef } = program;

  // --- Core forward navigation (god-view; reads refs across features) ---
  // Asks the manager (resolveOnboardingRoute) where the user belongs now, then —
  // when that's the survey start and the caller declared its position — steps
  // forward through the survey group order. Reads inputs via refs so the callback
  // identity stays stable across renders (an unstable identity used to cascade
  // into eligibility-check loops in CompanySearchScreen). The eligibility-only
  // inputs aren't consulted in forward nav, so they're passed as inert defaults.
  const goToNextStep = useCallback(
    (options?: GoToNextStepOptions) => {
      const authStatus = authStatusRef.current;
      if (authStatus === 'loading') return setCurrentRoute(null);
      const userData = userDataRef.current;
      // No account yet (unauthenticated, or a user without a customer profile).
      if (authStatus === 'unauthenticated' || !userData?.customerProfile) {
        return setCurrentRoute('/step/account-creation');
      }

      const fromPath = hrefPathname(options?.from);

      // Program-matching sub-flow forward step (coaching-method → review). The
      // list → next branch is selection-driven (selectProgram); entering the
      // program region from the survey is the driver's job.
      const programNext = programMatchingNextStep(fromPath);
      if (programNext) return setCurrentRoute(programNext as Href);

      const toResource = (value: boolean | undefined): ResourceState<boolean> =>
        value === undefined ? UNSET : { status: 'ready', value };

      const resolution = resolveOnboardingRoute({
        authStatus,
        user: userData,
        orgParam: undefined,
        // Forward nav already has a resolved selectedOrg; the SSO gate is only
        // relevant on a cold resolve before the catalog loads.
        ssoWebserverOrgUuid: undefined,
        partners: { status: 'ready', data: [], error: null },
        selectedOrg: selectedOrgRef.current,
        eligibilityCheckResult: eligibilityCheckResultRef.current,
        // Forward nav runs only after an account exists, past the PURL pre-account
        // stage — an inert default keeps the resolver's PURL branch dormant here.
        purl: {
          status: 'unset',
          token: undefined,
          orgName: undefined,
          verifiedDob: undefined,
          redirectUrl: undefined,
        },
        programInstances: toResource(hasProgramRef.current),
        metricPoints: toResource(surveyCompleteRef.current),
        teams: UNSET,
        prescribedPrograms: UNSET,
        // The survey start is arm-dependent, so forward nav mirrors the live flag
        // resource + error latch (synced in the effects cluster below).
        dynamicAssessment: dynamicAssessmentEnabledRef.current,
        dynamicAssessmentErrored: dynamicAssessmentErroredRef.current,
        requiresCrisisLine: requiresCrisisLineRef.current,
        showTestimonialPage: showTestimonialPageRef.current,
        // Inert on forward nav: `teams: UNSET` above makes the resolver bail with
        // need('teams') before the coach branch ever reads showMemberContact, so
        // the value is never consulted here (the cold-resolve path uses the live
        // computed value). Pass a literal `false` rather than mirror a dead ref.
        showMemberContact: false,
      });
      // Resources are resolved by the time a screen calls goToNextStep, so a
      // `need` shouldn't occur in forward nav — bail rather than misroute.
      if (resolution.kind === 'need') return;
      // Narrowed to the route variant here, so `href` is safe to destructure.
      const { href: baseRoute } = resolution;

      // The survey region parked us at its start — either arm's: the legacy walk
      // starts at /step/health-conditions, the dynamic-assessment (test) arm at
      // /step/dynamic-assessment. Which one came back also tells us whether the
      // dynamic pass served the whole region (→ skip the standalone pre-diabetes
      // / phq-gad steps).
      const basePath = hrefPathname(baseRoute);
      if (
        (basePath === '/step/health-conditions' || basePath === '/step/dynamic-assessment') &&
        fromPath
      ) {
        // Dynamic survey: skip the screeners the org doesn't require (org-aware,
        // not the linear group order). null → the dynamic survey is exhausted:
        // a crisis outcome deferred by the dynamic phq_gad flow routes to the
        // crisis program screen (enterCrisisProgram marks the survey complete
        // itself); otherwise mark it complete and let the driver re-resolve into
        // the program region (same effect as the phq-gad screen's
        // markSurveyComplete).
        if (isDynamicSurveyStep(fromPath)) {
          const org = userDataRef.current?.payingOrganization;
          const servedFlows = dynamicCompletedFlowKeysRef.current;
          const next = dynamicSurveyNextStep(fromPath, {
            preDiabetesRequired: org?.preDiabetesRequired,
            phqGadRequired: org?.phqGadRequired,
            // Per-survey completion from the dynamic pass's /flows/ snapshot, NOT
            // "were we in the test arm" (basePath): a flow that was never
            // published or landed `abandoned` never reaches `completed`, so its
            // standalone screener still runs instead of being silently dropped.
            // Keys are DYNAMIC_ONBOARDING_FLOW_KEYS members.
            preDiabetesServed: servedFlows.includes('pre_diabetes_v1'),
            phqGadServed: servedFlows.includes('phq_gad_v1'),
          });
          if (next) return setCurrentRoute(next as Href);
          const pendingCrisisUrns = pendingCrisisUrnsRef.current;
          if (pendingCrisisUrns && pendingCrisisUrns.length > 0) {
            return enterCrisisProgram(pendingCrisisUrns);
          }
          return markSurveyComplete();
        }
        // Static survey (conditions → goals → screeners) is a fixed linear walk.
        const next = findNextStep(fromPath);
        if (next) return setCurrentRoute(next as Href);
      }

      setCurrentRoute(baseRoute);
    },
    [selectedOrgRef, markSurveyComplete, enterCrisisProgram, pendingCrisisUrnsRef]
  );

  // --- Reset orchestration (sits above the feature hooks; calls their resets) ---
  // Clears the per-user, driver-fetched resources + the feature picks. Shared by
  // `resetUserData` (sign-out / 401) and the user-identity-change effect. Does NOT
  // touch auth/draft/route or the identify/completion latches (those differ per
  // caller and stay inline). `partners` is left alone (cached app-lifetime).
  const resetPerUserResources = useCallback(() => {
    resetResources();
    resetProgram();
    resetCoach();
    setMemberContactDismissed(false);
  }, [resetResources, resetProgram, resetCoach]);

  const resetUserData = useCallback(() => {
    setUserData(undefined);
    setAuthStatus('unauthenticated');
    setDraft({ ...DEFAULT_ONBOARDING_DRAFT });
    setEligibilityCheckResult(undefined);
    resetPerUserResources();
    // Allow a future user to identify after this one logs out / 401s.
    hasIdentifiedRef.current = false;
    // Point currentRoute back at account-creation immediately so a mid-session
    // 401 doesn't leave a frame on a protected step before the driver re-runs.
    // (stepHistory is cleared by OnboardingStepsProvider when authStatus flips
    // to 'unauthenticated' — it lives in the child.)
    setCurrentRoute('/step/account-creation');
  }, [resetPerUserResources, setEligibilityCheckResult]);

  // The single onboarding-completion chokepoint — Sparta's `onboardingComplete()`
  // → `checkOnboardingCompleteEpic`. Invoked once when the user reaches the
  // terminal transition screen (the convergence of both arrivals: the driver
  // resolving a returning user who already has a coach, and Quick Intro Messages
  // advancing forward). Fires the `onboarding-completed` analytics event and the
  // desktop web-app redirect. Guarded to run at most once per user session.
  const completeOnboarding = useCallback(async () => {
    if (onboardingCompletedRef.current) return;
    const user = userDataRef.current;
    if (!user) return;
    onboardingCompletedRef.current = true;
    trackEvent('onboarding-completed', {
      uuid: user.uuid,
      payingOrgUrn: user.payingOrganization?.urn,
    });
    // Native webview: STOP — the native exit (sesame handoff + WebView dismiss)
    // is owned by NativeHandoffScreen, which the transition route renders and
    // which runs it on mount. This must return before
    // maybeRedirectToWebApp: in a native webview `Platform.OS` is `'web'`
    // (react-native-web), so the web redirect would otherwise fire here
    // (Sparta's onboardingCompleteEpic returns EMPTY for native).
    if (getNativeAppClientInfo().isLoadedInNativeApp) {
      return;
    }
    // Desktop users on the experiment leave onboarding for the web app instead of
    // the transition screen (Sparta's checkOnboardingCompleteEpic). No-op off the
    // experiment. Best-effort — a redirect failure must never surface as an
    // unhandled rejection from the fire-and-forget caller.
    try {
      await maybeRedirectToWebApp();
    } catch {
      // Swallow — completion already fired; staying on the transition screen is
      // the safe fallback.
    }
  }, []);

  // --- Effects cluster ---
  // Sync feature/core state into the core-owned mirror refs so the stable
  // callbacks above read fresh values. Runs after every render. (selectedOrgRef +
  // the feature hooks' own refs are synced inside their hooks.)
  useEffect(() => {
    authStatusRef.current = authStatus;
    userDataRef.current = userData;
    currentRouteRef.current = currentRoute;
    eligibilityCheckResultRef.current = eligibilityCheckResult;
    hasProgramRef.current =
      programInstances.status === 'ready' ? programInstances.value : undefined;
    surveyCompleteRef.current = metricPoints.status === 'ready' ? metricPoints.value : undefined;
    requiresCrisisLineRef.current = program.requiresCrisisLine;
    showTestimonialPageRef.current = program.showTestimonialPage;
    dynamicAssessmentEnabledRef.current = dynamicAssessmentEnabled;
    dynamicAssessmentErroredRef.current = dynamicAssessmentErrored;
    dynamicCompletedFlowKeysRef.current = dynamicCompletedFlowKeys;
  });

  // Re-arm the per-user resources when the user identity / eligibility state
  // changes (fresh sign-in, eligibility just granted), so the driver refetches
  // against the new user instead of reading another user's cached values.
  const userKey = userData
    ? `${userData.resourceUri}|${userData.payingOrganization ? 'org' : 'no-org'}`
    : 'none';
  const userKeyRef = useRef(userKey);
  useEffect(() => {
    if (userKeyRef.current === userKey) return;
    userKeyRef.current = userKey;
    resetPerUserResources();
    onboardingCompletedRef.current = false;
  }, [userKey, resetPerUserResources]);

  // Analytics identify (once per session) + load-derived person-property
  // backfill. `hasIdentifiedRef` stays owned here (resetUserData clears it).
  useAnalyticsIdentify({ authStatus, userData, orgParam, hasIdentifiedRef });

  // Bootstrap: runs once on mount. Validates the stored token (silent refresh if
  // stale), then delegates to refreshUser. The driver effect below routes from the
  // resulting state — including the unauthenticated case.
  useEffect(() => {
    let cancelled = false;
    (async function bootstrap() {
      let token = await getValidAccessToken();
      if (!token) {
        // Native-WebView entry: Sparta's broker redirected here with a
        // one-time `?handoff=` code (Apigee session-exchange). Redeeming it
        // stores a Bearer pair, letting a resuming native user land
        // authenticated. No-op for every other entry (param absent), and a
        // failed redemption falls through to the unauthenticated flow.
        if (await redeemNativeHandoff()) {
          token = await getValidAccessToken();
        }
      }
      if (!token) {
        if (!cancelled) setAuthStatus('unauthenticated');
        return;
      }
      try {
        await refreshUser();
      } catch {
        if (!cancelled) resetUserData();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUser, resetUserData]);

  // Subscribe to 401 events from apiClient. A revoked/expired token mid-session
  // resets to unauthenticated; the driver routes to account-creation.
  useEffect(() => {
    return subscribeToUnauthorized(resetUserData);
  }, [resetUserData]);

  // THE DRIVER — the single owner of resumption routing. Runs the manager
  // (resolveOnboardingRoute) over current state; on a `need` it fetches that
  // resource (staying on the spinner) and re-runs when it lands; on a `route` it
  // writes currentRoute. Loops resolve → fetch → re-resolve until a route emerges,
  // identically for every entry path. Does NOT depend on currentRoute, so the
  // imperative survey advance (goToNextStep) is never clobbered.
  // The manager's verdict over current durable state, memoized so the driver
  // effect AND `isResolving` share one evaluation. null while auth is loading or
  // an eligibility check is mid-flight — the firing screen shows its own spinner.
  const resolution = useMemo<Resolution | null>(() => {
    if (authStatus === 'loading') return null;
    if (eligibilityCheckResult?.isChecking) return null;
    // "teams" is misnamed, it actually represents a hasCoach boolean,
    // which is currently hardcoded to soloTeam (likely a bug)
    if (memberContactFlag.isLoading && teams.status === 'ready' && teams.value) {
      return null;
    }
    return resolveOnboardingRoute({
      authStatus,
      user: userData,
      orgParam: shouldReadOrgParam ? orgParam : undefined,
      ssoWebserverOrgUuid,
      partners,
      selectedOrg,
      eligibilityCheckResult,
      purl,
      programInstances,
      metricPoints,
      teams,
      prescribedPrograms,
      dynamicAssessment: dynamicAssessmentEnabled,
      dynamicAssessmentErrored,
      requiresCrisisLine: program.requiresCrisisLine,
      showTestimonialPage: program.showTestimonialPage,
      showMemberContact,
    });
  }, [
    authStatus,
    userData,
    orgParam,
    shouldReadOrgParam,
    ssoWebserverOrgUuid,
    partners,
    selectedOrg,
    eligibilityCheckResult,
    purl,
    programInstances,
    metricPoints,
    teams,
    prescribedPrograms,
    dynamicAssessmentEnabled,
    dynamicAssessmentErrored,
    program.requiresCrisisLine,
    program.showTestimonialPage,
    showMemberContact,
    memberContactFlag.isLoading,
  ]);

  useEffect(() => {
    if (!resolution) return;
    if (resolution.kind === 'need') {
      // Only KICK a fetch when the resource is still unset; while it's already
      // 'loading' the driver simply waits. Re-invoking the loader on every
      // loading-state re-render would churn `setState(loading)` and spin the effect.
      switch (resolution.resource) {
        case 'partners':
          // userData is guaranteed here — the resolver only asks for partners past
          // account-creation. Pass its live `isTester`.
          if (partners.status === 'unset') void loadPartners(userData?.isTester ?? false);
          break;
        case 'programInstances':
          if (programInstances.status === 'unset' && userData) {
            ensureProgramInstances(userData.resourceUri);
          }
          break;
        case 'metricPoints':
          if (metricPoints.status === 'unset') ensureMetricPoints();
          break;
        case 'teams':
          if (teams.status === 'unset' && userData) {
            ensureTeams(userData.customerProfile?.soloTeamCode);
          }
          break;
        case 'dynamicAssessment':
          // The survey start is arm-dependent — read the flag before routing.
          // ensureDynamicAssessment dedupes itself, and a failed read settles to
          // 'error' (fails open to the legacy walk), so no unset-guard is needed;
          // guard anyway for symmetry with the other resources.
          if (dynamicAssessmentEnabled.status === 'unset') ensureDynamicAssessment();
          break;
      }
      return;
    }
    // Entering the program region: kick the prescribed-programs catalog fetch
    // WITHOUT holding — the resolver already routed us here, so the screen mounts
    // and shows a skeleton while the catalog loads (vs. blocking on the spinner).
    if (
      (resolution.href === '/step/program-matching' ||
        resolution.href === '/step/primary-recommendation-program-detail-screen') &&
      prescribedPrograms.status === 'unset' &&
      userData
    ) {
      ensurePrescribedPrograms(userData.urn);
    }
    // Same non-blocking kick for the coach region: route to coach-matching now,
    // fetch the matched-coaches catalog in the background, screen skeletons.
    if (
      resolution.href === '/step/coach-matching' &&
      matchedCoaches.status === 'unset' &&
      userData?.customerProfile?.resourceUri
    ) {
      ensureMatchedCoaches(userData.customerProfile.resourceUri);
    }
    // A *manual* in-session org selection (no deep-link `?org=`) must not
    // auto-navigate the user off company-search to that org's eligibility entry
    // screen — they stay on the search form and press Continue (goToNextStep) to
    // advance. Only a deep-link hint (`orgParam`) auto-routes.
    const nextPath = hrefPathname(resolution.href);
    if (
      !orgParam &&
      hrefPathname(currentRouteRef.current) === '/step/company-search' &&
      (nextPath === '/step/employee-zip-verification' ||
        nextPath === '/step/employer-or-health-plan-verification')
    ) {
      return;
    }
    setCurrentRoute(resolution.href);
  }, [
    resolution,
    orgParam,
    userData,
    partners,
    programInstances,
    metricPoints,
    teams,
    prescribedPrograms,
    matchedCoaches,
    dynamicAssessmentEnabled,
    loadPartners,
    ensureProgramInstances,
    ensureMetricPoints,
    ensureTeams,
    ensurePrescribedPrograms,
    ensureMatchedCoaches,
    ensureDynamicAssessment,
  ]);

  // Reaching the terminal transition screen IS onboarding completion — run the
  // completion chokepoint. Watches `currentRoute` (not the driver's resolution)
  // so it covers both arrivals. `completeOnboarding` is self-guarding.
  useEffect(() => {
    if (hrefPathname(currentRoute) === '/step/transition-to-native-app') {
      void completeOnboarding();
    }
  }, [currentRoute, completeOnboarding]);

  // Busy while bootstrapping (no route yet) OR while the driver is still resolving
  // — a `need` means a routing resource is in flight and the destination isn't
  // known yet, so the layout shows the loading screen instead of flashing a prior
  // route underneath.
  const isResolving =
    authStatus === 'loading' || currentRoute === null || resolution?.kind === 'need';

  // Value is not memoized — its deps would cover every state slot and memoed
  // callback anyway. The inner useCallbacks keep references stable, which is what
  // actually prevents downstream effect / callback cascades in consumers.
  return (
    <RulesEngineContext.Provider
      value={{
        draft,
        setDraft,

        // Launch-context embed flags (captured once from the launch params).
        vidaClient: clientInfo.vidaClient,
        vidaClientVersion: clientInfo.vidaClientVersion,
        isLoadedInNativeApp: clientInfo.isLoadedInNativeApp,
        isLoadedInIframe: clientInfo.isLoadedInIframe,

        authStatus,
        currentRoute,
        setCurrentRoute,
        isResolving,

        user: userData,
        setUser,
        refreshUser,

        // `onb_use_dynamic_assessment` experiment, loaded as a resource (see
        // useOnboardingResources). The resolver reads it to pick the survey start
        // (test arm → /step/dynamic-assessment); AssignedScreenersScreen reads it
        // for the arm + readiness. `dynamicAssessmentErrored` +
        // `markDynamicAssessmentErrored` latch a mid-flight dynamic-pass failure,
        // which makes the resolver abandon the assessment region and skip to the
        // program region (all-or-nothing, no legacy fallback), and
        // `deferCrisisProgram` stashes a phq_gad_v1 crisis terminal until survey
        // exhaustion.
        dynamicAssessmentEnabled,
        ensureDynamicAssessment,
        dynamicAssessmentErrored,
        markDynamicAssessmentErrored,
        dynamicCompletedFlowKeys,
        setDynamicCompletedFlowKeys,
        deferCrisisProgram: program.deferCrisisProgram,

        partners,
        loadPartners,

        selectedOrg,
        setSelectedOrg,
        restartEligibility,

        runEligibilityCheck,
        eligibilityCheckResult,
        setEligibilityCheckResult,

        validateAndUseInviteCode,

        // PURL pre-authenticated enrollment — the DOB entry screen reads `purl` +
        // calls `verifyDob`; the account-creation screen reads `purl.verifiedDob`
        // + calls `submitPurlAccount`.
        purl,
        verifyDob,
        verifyAnotherWay,
        submitPurlAccount,

        // phq-gad marks the survey answered so the driver routes to program
        // selection rather than re-deriving to the survey start.
        markSurveyComplete,

        // Program matching (catalog + picks + enroll). The catalog + loading
        // flag are computed inside useProgramMatching.
        prescribedPrograms: program.prescribedPrograms,
        isLoadingPrograms: program.isLoadingPrograms,
        selectedProgramUuid: program.selectedProgramUuid,
        setSelectedProgram: program.setSelectedProgram,
        selectProgram: program.selectProgram,
        coachingModality: program.coachingModality,
        setCoachingModality: program.setCoachingModality,
        enrollInProgram: program.enrollInProgram,
        requiresCrisisLine: program.requiresCrisisLine,
        enterCrisisProgram: program.enterCrisisProgram,
        showTestimonialPage: program.showTestimonialPage,
        dismissTestimonial: program.dismissTestimonial,

        // Member Contact experiment (ME-1256). `dismissMemberContact` clears the
        // step (its Continue/Skip) so the driver advances to the terminal. The
        // routing gate lives in the resolver (`showMemberContact` input) and the
        // coach exit reads the split directly — neither needs it on the value.
        dismissMemberContact,

        // Coach matching (catalog + picks + assign/intro). The catalog/consult
        // + loading flag are computed inside useCoachMatching.
        matchedCoaches: coach.matchedCoaches,
        isLoadingCoaches: coach.isLoadingCoaches,
        selectedCoachUuid: coach.selectedCoachUuid,
        setSelectedCoach: coach.setSelectedCoach,
        scheduledConsult: coach.scheduledConsult,
        setScheduledConsult: coach.setScheduledConsult,
        assignPrimaryCoach: coach.assignPrimaryCoach,
        sendIntroMessage: coach.sendIntroMessage,
        // Set at the coach-phase exit (QuickIntroMessages `advance`) so the driver
        // knows provider matching is complete without a refetch — prevents a later
        // re-resolve (e.g. member-contact's mount refresh) from bouncing back to
        // coach matching off a stale `teams` (hasCoach) value.
        markProviderMatchingComplete,

        // Shared per-provider availability cache + its loader. Consumed by the
        // coach cards, the matching skip decision, and the availability screen.
        coachAvailability,
        ensureCoachAvailability,

        goToNextStep,
      }}
    >
      {children}
    </RulesEngineContext.Provider>
  );
};
